import * as React from 'react';
import logo from '../../assets/img/logo_alt.png';
import { useSnackbar, withSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import SignUp from './SignUp';
import { useSearchParams } from 'react-router-dom';
import FormLogin from './FormLogin';
import FormCredentialsSent from './FormCredentialsSent';
import FormChangePass from './FormChangePass';
import FormForgotPass from './FormForgotPass';
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from './firebase';
import { ApiRequest } from 'GlobalFunctions';
import { useAuth } from 'contexts/AuthContext';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, TextField } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';

/**
 * SignIn — componente raíz del flujo de autenticación.
 *
 * Orquesta los sub-formularios: login, registro, recuperación de contraseña,
 * ingreso con Google y selección de rol post-Google.
 *
 * Cambios respecto a la versión anterior:
 * - Reemplaza todas las llamadas directas a `axios` por `ApiRequest` para
 *   consistencia y manejo de errores centralizado.
 * - Usa `useAuth().login()` para persistir la sesión en contexto y localStorage
 *   simultáneamente.
 */
export default withSnackbar(
  function SignIn(props: any) {
    const { setLoading } = props;
    const { enqueueSnackbar } = useSnackbar();
    const { login } = useAuth();

    const [loadingButton, setLoadingButton] = useState(false);
    const [openRegister, setOpenRegister] = useState(false);
    const [state, setState] = useState('login');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [pendingGoogleUser, setPendingGoogleUser] = useState<any>(null);
    const [nursePhone, setNursePhone] = useState('');
    const [nurseAlertOpen, setNurseAlertOpen] = useState(false);
    const [searchParams] = useSearchParams();

    useEffect(() => {
      setLoading(false);
      setEmail(localStorage.getItem('email') || searchParams.get('email') || '');
      setCode(searchParams.get('code') || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loginDataDefault = { email, password: '' };
    const [loginData, setLoginData] = useState(loginDataDefault);

    /** Inicia sesión con email y contraseña */
    const sign_in = () => {
      setLoadingButton(true);
      ApiRequest({
        url: '/users/auth',
        method: 'POST',
        body: { email: loginData.email, password: loginData.password },
        setResponse: (response: any) => {
          if (response.status === 'success') {
            enqueueSnackbar(response.message, { variant: 'success' });
            login({
              ltkn: response.user_info.ltkn,
              user_name: response.user_info.user_name,
              role_id: Number(response.user_info.role_id),
            });
            window.location.href = '/';
          } else {
            enqueueSnackbar(response.message || 'Credenciales incorrectas', { variant: 'error' });
            setLoadingButton(false);
          }
        },
        onError: (msg) => {
          enqueueSnackbar(msg, { variant: 'error' });
          setLoadingButton(false);
        },
      });
    };

    /** Inicia sesión con Google OAuth */
    const signInWithGoogle = async () => {
      try {
        setLoadingButton(true);
        const result = await signInWithPopup(auth, googleProvider);
        const user = result.user;

        await ApiRequest({
          url: '/users/google-auth',
          method: 'POST',
          body: { email: user.email, name: user.displayName, google_id: user.uid },
          setResponse: (response: any) => {
            if (response.status === 'success') {
              login({
                ltkn: response.user_info.ltkn,
                user_name: response.user_info.user_name,
                role_id: Number(response.user_info.role_id),
              });
              window.location.href = '/';
            } else if (
              response.message?.includes('no encontrado') ||
              response.message?.includes('regístrese')
            ) {
              setPendingGoogleUser(user);
              setState('googleRoleSelect');
            } else {
              enqueueSnackbar(response.message, { variant: 'error' });
            }
            setLoadingButton(false);
          },
          onError: (msg) => {
            enqueueSnackbar(msg, { variant: 'error' });
            setLoadingButton(false);
          },
        });
      } catch {
        enqueueSnackbar('Error al iniciar sesión con Google', { variant: 'error' });
        setLoadingButton(false);
      }
    };

    /** Completa el registro de un nuevo usuario de Google con rol seleccionado */
    const completeGoogleSignup = async (role_id: number, phone: string = '') => {
      if (!pendingGoogleUser) return;
      setLoadingButton(true);
      await ApiRequest({
        url: '/users/google-signup',
        method: 'POST',
        body: {
          email: pendingGoogleUser.email,
          name: pendingGoogleUser.displayName?.split(' ')[0] || '',
          lastname: pendingGoogleUser.displayName?.split(' ').slice(1).join(' ') || '',
          google_id: pendingGoogleUser.uid,
          role_id,
          phone,
        },
        setResponse: (response: any) => {
          if (response.status === 'success') {
            login({
              ltkn: response.user_info.ltkn,
              user_name: response.user_info.user_name,
              role_id: Number(response.user_info.role_id),
            });
            if (role_id === 2) {
              setNurseAlertOpen(true);
            } else {
              window.location.href = '/';
            }
          } else {
            enqueueSnackbar(response.message, { variant: 'error' });
            setState('login');
          }
          setLoadingButton(false);
        },
        onError: (msg) => {
          enqueueSnackbar(msg, { variant: 'error' });
          setLoadingButton(false);
        },
      });
    };

  return (
  <>  

      <div id="AuthContainer">
        {state === "login" && (
          <FormLogin
            setLoading={setLoading}
            setState={setState}
            loginData={loginData}
            setEmail={setEmail}
            setLoginData={setLoginData}
            loadingButton={loadingButton}
            setLoadingButton={setLoadingButton}
            setOpenRegister={setOpenRegister}
            sign_in={sign_in}
            logo={logo}
            onGoogleSignIn={signInWithGoogle}
          />
        )}
        {state === "changePass" && (
          <FormChangePass
            loadingButton={loadingButton}
            setLoading={setLoading}
            setState={setState}
            email={email}
            code={code}
          />
        )}
        {state === "forgotPass" && (
          <FormForgotPass
            loadingButton={loadingButton}
            setLoading={setLoading}
            setState={setState}
            email={email}
            setEmail={setEmail}
          />
        )}
        {state === "credentialsSent" && (
          <FormCredentialsSent
            logo={logo}
            setState={setState}
            email={email}
            code={code}
            setCode={setCode}
            setLoginData={setLoginData}
            loginDataDefault={loginDataDefault}
          />
        )}

        {state === "signup" && (
          <SignUp
            openRegister={openRegister}
            setOpenRegister={setOpenRegister}
            loginData={loginData}
            setLoginData={setLoginData}
            sign_in={sign_in}
            setLoading={setLoading}
          />
        )}

        {state === "googleRoleSelect" && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24, padding: '40px 20px', maxWidth: 420, margin: '0 auto' }}>
            <img src={logo} style={{ height: 56, marginBottom: 8 }} alt="logo" />
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', margin: '0 0 8px 0' }}>¿Cómo quieres registrarte?</h2>
              <p style={{ color: '#64748b', fontSize: 14, margin: 0 }}>
                Hola <b>{pendingGoogleUser?.displayName}</b>, selecciona tu tipo de cuenta
              </p>
            </div>
            <div style={{ display: 'flex', gap: 16, width: '100%' }}>
              <button
                onClick={() => completeGoogleSignup(1)}
                disabled={loadingButton}
                style={{
                  flex: 1, padding: '24px 16px', border: '2px solid #e2e8f0', borderRadius: 14,
                  background: '#fff', cursor: 'pointer', transition: 'all 0.15s', textAlign: 'center',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#135095'; (e.currentTarget as HTMLButtonElement).style.background = '#f0f6ff'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#e2e8f0'; (e.currentTarget as HTMLButtonElement).style.background = '#fff'; }}
              >
                <div style={{ fontSize: 32, marginBottom: 8 }}>🧑‍⚕️</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', marginBottom: 4 }}>Paciente</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>Busco enfermeras especialistas</div>
              </button>
              <button
                onClick={() => setState('googleNursePhone')}
                disabled={loadingButton}
                style={{
                  flex: 1, padding: '24px 16px', border: '2px solid #e2e8f0', borderRadius: 14,
                  background: '#fff', cursor: 'pointer', transition: 'all 0.15s', textAlign: 'center',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#135095'; (e.currentTarget as HTMLButtonElement).style.background = '#f0f6ff'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#e2e8f0'; (e.currentTarget as HTMLButtonElement).style.background = '#fff'; }}
              >
                <div style={{ fontSize: 32, marginBottom: 8 }}>👩‍⚕️</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', marginBottom: 4 }}>Enfermera</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>Ofrezco servicios de curación</div>
              </button>
            </div>
            <button
              onClick={() => { setState('login'); setPendingGoogleUser(null); }}
              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 13 }}
            >
              ← Volver al inicio de sesión
            </button>
          </div>
        )}

        {state === 'googleNursePhone' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, padding: '40px 20px', maxWidth: 420, margin: '0 auto', width: '100%' }}>
            <img src={logo} style={{ height: 56, marginBottom: 4 }} alt="logo" />
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', margin: '0 0 8px 0' }}>Último paso</h2>
              <p style={{ color: '#64748b', fontSize: 14, margin: 0 }}>
                Hola <b>{pendingGoogleUser?.displayName}</b>, necesitamos tu número de teléfono para completar el registro.
              </p>
            </div>
            <TextField
              label="Número de teléfono"
              value={nursePhone}
              onChange={(e) => setNursePhone(e.target.value)}
              fullWidth
              variant="outlined"
              placeholder="+56 9 XXXX XXXX"
              inputProps={{ inputMode: 'tel' }}
            />
            <LoadingButton
              fullWidth
              variant="contained"
              disabled={!nursePhone.trim() || loadingButton}
              loading={loadingButton}
              onClick={() => completeGoogleSignup(2, nursePhone)}
              sx={{ padding: '12px 0', fontWeight: 700 }}
            >
              Completar registro
            </LoadingButton>
            <button
              onClick={() => setState('googleRoleSelect')}
              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 13 }}
            >
              ← Volver
            </button>
          </div>
        )}
      </div>

      {/* Alerta de certificados para enfermeras recién registradas */}
      <Dialog open={nurseAlertOpen} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 19, pb: 0 }}>
          🎉 ¡Registro exitoso!
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 30, flexShrink: 0 }}>📋</span>
            <p style={{ margin: 0, color: '#374151', lineHeight: 1.7, fontSize: 15 }}>
              Recuerda subir tus <strong>certificados y credenciales profesionales</strong> en tu perfil para quedar aprobada como Especialista en Heridas. De lo contrario, nos contactaremos contigo para solicitarlos.
            </p>
          </div>
        </DialogContent>
        <DialogActions sx={{ p: '12px 24px 20px' }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => { setNurseAlertOpen(false); window.location.href = '/'; }}
          >
            Entendido, ir a mi perfil
          </Button>
        </DialogActions>
      </Dialog>

  </>
  );
}
);